/**
 * Send Copy Invoice Dialog Component
 * Allows users to send copy invoices to customers via email
 */

import { useState, useEffect } from "react";
import { Mail, AlertCircle, CheckCircle2, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { type Transaction } from "@/types/transaction";
import { formatCurrency, formatDateDDMMYYYY } from "@/lib/utils";
import { useLogSendCopyInvoice } from "@/hooks/useLogSendCopyInvoice";
import { toast } from "sonner";

interface SendCopyInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactions: Transaction[];
  onSend: (email: string, message: string, transactionIds: number[]) => void;
}

export function SendCopyInvoiceDialog({
  open,
  onOpenChange,
  transactions,
  onSend,
}: SendCopyInvoiceDialogProps) {
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Filter for invoices only
  const invoices = transactions.filter((t) => t.transactionType === "invoice");

  // Get primary contact email (mock - in real app would fetch from customer data)
  const getPrimaryContactEmail = (customerId: number): string => {
    // Mock primary contact emails based on customer
    const mockEmails: Record<number, string> = {
      1: "sarah.mitchell@acmecorp.com",
      2: "robert.williams@globaltech.com",
      3: "emily.rodriguez@megaretail.com",
      4: "james.anderson@techsolutions.com",
      5: "lisa.thompson@innovativedesigns.com",
    };
    return mockEmails[customerId] || "accounts@customer.com";
  };

  // Get Copy Invoice Request template
  const getCopyInvoiceTemplate = () => {
    // This matches the template from document-templates.tsx
    return {
      subject: 'Copy Invoice(s) - Account {{customer.accountNumber}}',
      content: `<p>Dear {{contact_firstName}},</p><p>Thank you for your request for copy invoice(s).</p><p>As requested, please find attached the following invoice(s) for your account {{customer.accountNumber}}:</p><ul><li>Invoice {{invoice.number}} dated {{invoice.date}} - Amount: {{invoice.amount}}</li></ul><p>If you require any additional invoices or have questions regarding these documents, please don't hesitate to contact us.</p><p><strong>Account Summary:</strong><br>Account Number: {{customer.accountNumber}}<br>Current Balance: {{account.balance}}<br>Payment Due Date: {{account.dueDate}}</p><p>For your convenience, you can make payment using the following methods:</p><ul><li>Online: {{payment.link}}</li><li>Bank Transfer: {{company.bankDetails}}</li><li>Phone: {{company.phone}}</li></ul><p>If you have already made payment, please disregard the balance information above.</p><p>Thank you for your business.</p><p>Best regards,<br>{{creditController.firstName}} {{creditController.lastName}}<br>{{company.name}}<br>Email: {{creditController.email}}<br>Phone: {{company.phone}}</p>`
    };
  };

  // Convert HTML template to plain text
  const htmlToPlainText = (html: string): string => {
    return html
      .replace(/<\/p>/g, '\n\n')
      .replace(/<br\s*\/?>/g, '\n')
      .replace(/<\/li>/g, '\n')
      .replace(/<li>/g, '• ')
      .replace(/<ul>/g, '\n')
      .replace(/<\/ul>/g, '\n')
      .replace(/<strong>(.*?)<\/strong>/g, '$1')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  };

  // Replace template variables with actual data
  const replaceTemplateVariables = (template: string, invoice: Transaction): string => {
    // Mock data for template variables
    const mockData = {
      contact_firstName: 'Customer',
      customer: {
        accountNumber: invoice.customerId.toString().padStart(6, '0'),
      },
      invoice: {
        number: invoice.referenceNumber,
        date: formatDateDDMMYYYY(invoice.transactionDate),
        amount: formatCurrency(invoice.amount, invoice.currency),
      },
      account: {
        balance: formatCurrency(invoice.outstandingAmount || 0, invoice.currency),
        dueDate: invoice.dueDate ? formatDateDDMMYYYY(invoice.dueDate) : 'N/A',
      },
      payment: {
        link: 'https://payments.example.com',
      },
      company: {
        name: 'Your Company Name',
        bankDetails: 'Bank: Example Bank, Account: 12345678, Sort Code: 12-34-56',
        phone: '+1 (555) 123-4567',
      },
      creditController: {
        firstName: 'John',
        lastName: 'Smith',
        email: 'john.smith@company.com',
      },
    };

    return template
      .replace(/\{\{contact_firstName\}\}/g, mockData.contact_firstName)
      .replace(/\{\{customer\.accountNumber\}\}/g, mockData.customer.accountNumber)
      .replace(/\{\{invoice\.number\}\}/g, mockData.invoice.number)
      .replace(/\{\{invoice\.date\}\}/g, mockData.invoice.date)
      .replace(/\{\{invoice\.amount\}\}/g, mockData.invoice.amount)
      .replace(/\{\{account\.balance\}\}/g, mockData.account.balance)
      .replace(/\{\{account\.dueDate\}\}/g, mockData.account.dueDate)
      .replace(/\{\{payment\.link\}\}/g, mockData.payment.link)
      .replace(/\{\{company\.bankDetails\}\}/g, mockData.company.bankDetails)
      .replace(/\{\{company\.phone\}\}/g, mockData.company.phone)
      .replace(/\{\{creditController\.firstName\}\}/g, mockData.creditController.firstName)
      .replace(/\{\{creditController\.lastName\}\}/g, mockData.creditController.lastName)
      .replace(/\{\{company\.name\}\}/g, mockData.company.name)
      .replace(/\{\{creditController\.email\}\}/g, mockData.creditController.email);
  };

  // Pre-populate email and subject when dialog opens
  useEffect(() => {
    if (open && invoices.length > 0) {
      // Get the first customer's primary contact email
      const primaryEmail = getPrimaryContactEmail(invoices[0].customerId);
      setRecipientEmail(primaryEmail);
      
      // Get template
      const template = getCopyInvoiceTemplate();
      
      // Replace variables in subject
      const populatedSubject = replaceTemplateVariables(template.subject, invoices[0]);
      setEmailSubject(populatedSubject);
      
      // Replace variables in content and convert to plain text
      const populatedContent = replaceTemplateVariables(template.content, invoices[0]);
      const plainTextContent = htmlToPlainText(populatedContent);
      setEmailMessage(plainTextContent);
      
      setEmailError("");
    }
  }, [open, invoices]);

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (email: string) => {
    setRecipientEmail(email);
    if (email && !validateEmail(email)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const logSendCopyInvoiceMutation = useLogSendCopyInvoice();

  const handleSend = async () => {
    if (!recipientEmail || !validateEmail(recipientEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    if (invoices.length === 0) {
      return;
    }

    setIsSending(true);

    try {
      // Log the send copy invoice activity
      await logSendCopyInvoiceMutation.mutateAsync({
        customerId: invoices[0].customerId,
        recipientEmail,
        message: emailMessage || undefined,
        transactionIds: invoices.map((inv) => inv.id),
      });

      onSend(
        recipientEmail,
        emailMessage,
        invoices.map((inv) => inv.id)
      );

      // Reset state
      setEmailMessage("");
      setEmailSubject("");
      setRecipientEmail("");
      setEmailError("");
      onOpenChange(false);
    } catch (error) {
      toast.error(`Failed to send invoice: ${error instanceof Error ? error.message : 'Please try again.'}`);
    } finally {
      setIsSending(false);
    }
  };

  // Calculate totals
  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);
  const totalOutstanding = invoices.reduce(
    (sum, inv) => sum + (inv.outstandingAmount || 0),
    0
  );

  // Get customer name (all invoices should be from same customer)
  const customerName = invoices.length > 0 ? invoices[0].customerName : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Copy Invoice
          </DialogTitle>
          <DialogDescription>
            Send invoice copies to customer via email
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Validation Alert */}
          {invoices.length === 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No invoices selected. Please select at least one invoice to send.
              </AlertDescription>
            </Alert>
          )}

          {/* Customer Info Alert */}
          {invoices.length > 0 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Sending {invoices.length} invoice{invoices.length !== 1 ? "s" : ""} for{" "}
                <strong>{customerName}</strong>
              </AlertDescription>
            </Alert>
          )}

          {/* Email Recipient Section */}
          <div className="space-y-2">
            <Label htmlFor="recipient-email">
              Email Recipient
            </Label>
            <Input
              id="recipient-email"
              type="email"
              placeholder="customer@example.com"
              value={recipientEmail}
              onChange={(e) => handleEmailChange(e.target.value)}
              className={emailError ? "border-destructive" : ""}
            />
            {emailError && (
              <p className="text-sm text-destructive">{emailError}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Pre-populated with the primary contact email address. You can edit this if needed.
            </p>
          </div>

          {/* Email Subject Section */}
          <div className="space-y-2">
            <Label htmlFor="email-subject">
              Subject
            </Label>
            <Input
              id="email-subject"
              type="text"
              placeholder="Invoice Copy"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Customize the email subject line or use the default.
            </p>
          </div>

          {/* Email Content Section */}
          <div className="space-y-2">
            <Label htmlFor="email-message">
              Message
            </Label>
            <Textarea
              id="email-message"
              placeholder="Email message from Copy Invoice Request template..."
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows={8}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Pre-populated with the Copy Invoice Request template. You can customize the message as needed.
              Invoice attachments will be automatically included.
            </p>
          </div>

          <Separator />

          {/* Invoice Summary Section */}
          <div className="space-y-3">
            <Label>
              Invoice Summary ({invoices.length} invoice{invoices.length !== 1 ? "s" : ""})
            </Label>

            <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
              {invoices.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No invoices selected</p>
                </div>
              ) : (
                invoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-start gap-3 p-4 hover:bg-muted/30 transition-colors"
                  >
                    <div className="p-2 rounded-lg bg-primary/10 mt-0.5">
                      <FileText className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="font-semibold text-base">
                          {invoice.referenceNumber}
                        </span>
                        <Badge
                          variant={
                            invoice.status === "overdue"
                              ? "destructive"
                              : invoice.status === "outstanding"
                              ? "secondary"
                              : "default"
                          }
                          className="text-xs"
                        >
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span className="font-medium text-foreground">Date:</span>
                          <span>{formatDateDDMMYYYY(invoice.transactionDate)}</span>
                        </div>
                        {invoice.dueDate && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <span className="font-medium text-foreground">Due:</span>
                            <span>{formatDateDDMMYYYY(invoice.dueDate)}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span className="font-medium text-foreground">Amount:</span>
                          <span className="font-semibold text-foreground">
                            {formatCurrency(invoice.amount, invoice.currency)}
                          </span>
                        </div>
                        {invoice.outstandingAmount !== undefined && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <span className="font-medium text-foreground">Outstanding:</span>
                            <span
                              className={
                                invoice.outstandingAmount > 0
                                  ? "font-semibold text-orange-600"
                                  : "font-semibold text-green-600"
                              }
                            >
                              {formatCurrency(invoice.outstandingAmount, invoice.currency)}
                            </span>
                          </div>
                        )}
                      </div>
                      {invoice.description && (
                        <p className="text-sm text-muted-foreground mt-1.5 line-clamp-1">
                          {invoice.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Totals Summary */}
            {invoices.length > 0 && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Total Invoice Amount:</span>
                  <span className="font-bold text-base">
                    {formatCurrency(totalAmount, invoices[0].currency)}
                  </span>
                </div>
                {totalOutstanding > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">Total Outstanding:</span>
                    <span className="font-bold text-base text-orange-600">
                      {formatCurrency(totalOutstanding, invoices[0].currency)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Success Preview */}
          {invoices.length > 0 && recipientEmail && !emailError && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Ready to send <strong>{invoices.length}</strong> invoice
                {invoices.length !== 1 ? "s" : ""} to{" "}
                <strong>{recipientEmail}</strong>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setEmailMessage("");
              setEmailSubject("");
              setRecipientEmail("");
              setEmailError("");
              onOpenChange(false);
            }}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={
              invoices.length === 0 ||
              !recipientEmail ||
              !!emailError ||
              isSending
            }
            className="gap-2"
          >
            {isSending ? (
              <>
                <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4" />
                Send {invoices.length > 0 ? invoices.length : ""} Invoice
                {invoices.length !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
